import React, { useState } from 'react'
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, FormGroup, Label, Input, Alert, FormFeedback, Form } from 'reactstrap'
import gql from 'graphql-tag'
import { useMutation } from '@apollo/client'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { RepresentativeSchema } from './IncidentModalsSchema'

const CREATE_INCIDENT_PARTY_REPRESENTATIVE = gql`
  mutation CreateIncidentPartyRepresentative($input: CreateIncidentPartyRepresentativeMutationInput!) {
    createIncidentPartyRepresentative(input: $input) {
      ok
      representative {
        id
    }
      errors {
        field
        messages
      }
    }
  }
`

const AddRepresentativeModal = ({ isOpen, toggle, refetchRepresentatives, onCreate }) => {
    const [representativeError, setRepresentativeError] = useState(null)
    const [createRepresentative, { loading: creatingRepresentative }] = useMutation(CREATE_INCIDENT_PARTY_REPRESENTATIVE)
    const { control, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm({
        resolver: yupResolver(RepresentativeSchema),
        defaultValues: {
            name: '',
            businessName: '',
            phone: '',
            email: '',
            address: '',
            role: '',
        }
    })

    const handleCreateRepresentative = async (formData) => {
        setRepresentativeError(null)
        const input = { ...formData }
        try {
            const { data } = await createRepresentative({ variables: { input } })
            if (data?.createIncidentPartyRepresentative?.ok) {
                const createdRepresentative = {
                    id: data.createIncidentPartyRepresentative.representative.id,
                    name: formData.name,
                }
                await refetchRepresentatives()
                if (onCreate) onCreate(createdRepresentative)
                reset()
                handleClose()
            } else if (data?.createIncidentPartyRepresentative?.errors?.length) {
                setRepresentativeError(data.createIncidentPartyRepresentative.errors[0].messages[0])
            }
        } catch (e) {
            setRepresentativeError(e.message)
        }
    }

    const handleClose = () => {
        reset()
        setRepresentativeError(null)
        toggle()
    }

    return (
        <Modal isOpen={isOpen} toggle={handleClose}>
            <ModalHeader>Create Representative</ModalHeader>
            <ModalBody>
                {representativeError && <Alert color="danger">{representativeError}</Alert>}
                <Form id="add-representative-form" onSubmit={handleSubmit(handleCreateRepresentative)}>
                    <FormGroup>
                        <Label for="repName">Name*</Label>
                        <Controller
                            name="name"
                            control={control}
                            render={({ field }) => (
                                <Input
                                    id="repName"
                                    {...field}
                                    placeholder="Enter representative name"
                                    invalid={!!errors.name}
                                />
                            )}
                        />
                        <FormFeedback className="d-block">{errors.name?.message}</FormFeedback>
                    </FormGroup>
                    <FormGroup>
                        <Label for="repBusinessName">Business Name*</Label>
                        <Controller
                            name="businessName"
                            control={control}
                            render={({ field }) => (
                                <Input
                                    id="repBusinessName"
                                    {...field}
                                    placeholder="Enter business name"
                                    invalid={!!errors.businessName}
                                />
                            )}
                        />
                        <FormFeedback className="d-block">{errors.businessName?.message}</FormFeedback>
                    </FormGroup>
                    <FormGroup>
                        <Label for="repPhone">Phone</Label>
                        <Controller
                            name="phone"
                            control={control}
                            render={({ field }) => (
                                <Input
                                    id="repPhone"
                                    {...field}
                                    placeholder="Enter phone number"
                                    invalid={!!errors.phone}
                                />
                            )}
                        />
                        <FormFeedback className="d-block">{errors.phone?.message}</FormFeedback>
                    </FormGroup>
                    <FormGroup>
                        <Label for="repEmail">Email</Label>
                        <Controller
                            name="email"
                            control={control}
                            render={({ field }) => (
                                <Input
                                    id="repEmail"
                                    type="email"
                                    {...field}
                                    placeholder="Enter email"
                                    invalid={!!errors.email}
                                />
                            )}
                        />
                        <FormFeedback className="d-block">{errors.email?.message}</FormFeedback>
                    </FormGroup>
                    <FormGroup>
                        <Label for="repAddress">Address</Label>
                        <Controller
                            name="address"
                            control={control}
                            render={({ field }) => (
                                <Input
                                    id="repAddress"
                                    {...field}
                                    placeholder="Enter address"
                                    invalid={!!errors.address}
                                />
                            )}
                        />
                        <FormFeedback className="d-block">{errors.address?.message}</FormFeedback>
                    </FormGroup>
                    <FormGroup>
                        <Label for="repRole">Role</Label>
                        <Controller
                            name="role"
                            control={control}
                            render={({ field }) => (
                                <Input
                                    id="repRole"
                                    {...field}
                                    placeholder="Enter role"
                                    invalid={!!errors.role}
                                />
                            )}
                        />
                        <FormFeedback className="d-block">{errors.role?.message}</FormFeedback>
                    </FormGroup>
                </Form>
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" onClick={handleClose} disabled={creatingRepresentative || isSubmitting}>
                    Cancel
                </Button>
                <Button color="primary" type="submit" form="add-representative-form" disabled={creatingRepresentative || isSubmitting}>
                    {creatingRepresentative ? 'Creating...' : 'Create'}
                </Button>
            </ModalFooter>
        </Modal>
    )
}

export default AddRepresentativeModal
